import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { qualityAPI, apiErrorMessage, Concession, ConcessionDashboardStats, ConcessionDeviationType, ConcessionRiskLevel } from '../../lib/api';
import { usePagination } from '../../hooks';
import { getErrorMessage } from '../../hooks/useFetchData';
import { useIsSupplier, useIsCustomer } from '../../hooks/usePortalMode';
import { useView } from '../../contexts/ViewContext';
import { FIELD_MAPPING } from './constants';
import { toast } from 'sonner';

export interface NewConcessionForm {
  customer: string;
  part_number: string;
  part_description: string;
  quantity_affected: string;
  deviation_type: 'dimensional' | 'cosmetic' | 'material' | 'process' | 'documentation' | 'packaging' | 'other';
  title: string;
  description: string;
  reason: string;
  proposed_disposition: string;
  risk_level: 'low' | 'medium' | 'high' | 'critical';
  risk_justification: string;
}

const INITIAL_FORM: NewConcessionForm = {
  customer: '',
  part_number: '',
  part_description: '',
  quantity_affected: '',
  deviation_type: 'dimensional',
  title: '',
  description: '',
  reason: '',
  proposed_disposition: '',
  risk_level: 'medium',
  risk_justification: '',
};

export interface ConcessionFiltersState {
  status: string;
  riskLevel: string;
  deviationType: string;
  dateFrom: string;
  dateTo: string;
}

export const EMPTY_CONCESSION_FILTERS: ConcessionFiltersState = {
  status: '',
  riskLevel: '',
  deviationType: '',
  dateFrom: '',
  dateTo: '',
};

export function useConcessions() {
  const navigate = useNavigate();
  const isSupplier = useIsSupplier();
  const isCustomer = useIsCustomer();
  const { viewKey, getViewQueryParams } = useView();
  const [stats, setStats] = useState<ConcessionDashboardStats | null>(null);
  const [concessions, setConcessions] = useState<Concession[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState<ConcessionFiltersState>(EMPTY_CONCESSION_FILTERS);
  const [sortField, setSortField] = useState<string>('requested_date');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);
  // Kept separate from `error`: that one replaces the whole page, which is
  // right for a failed load and wrong for a failed create.
  const [createError, setCreateError] = useState<string | null>(null);
  const [newConcession, setNewConcession] = useState<NewConcessionForm>(INITIAL_FORM);

  const pagination = usePagination({
    pageSize: 15,
    onPageChange: () => {},
  });

  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
    pagination.reset();
  };

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const backendField = FIELD_MAPPING[sortField] || sortField;
      const ordering = sortDirection === 'desc' ? `-${backendField}` : backendField;

      const viewParams = getViewQueryParams();
      const statsParams: Record<string, string | number> = {};
      if (viewParams['site']) statsParams['site'] = viewParams['site'];
      if (viewParams['company']) statsParams['company'] = viewParams['company'];
      if (viewParams['group']) statsParams['group'] = viewParams['group'];
      const concessionParams: Record<string, string | number | undefined> = {
        ordering,
        page: pagination.currentPage,
        page_size: pagination.pageSize,
        search: searchTerm || undefined,
        ...viewParams,
      };
      if (filters.status) concessionParams['status'] = filters.status;
      if (filters.riskLevel) concessionParams['risk_level'] = filters.riskLevel;
      if (filters.deviationType) concessionParams['deviation_type'] = filters.deviationType;
      if (filters.dateFrom) concessionParams['requested_date__gte'] = filters.dateFrom;
      if (filters.dateTo) concessionParams['requested_date__lte'] = filters.dateTo;

      const [statsResponse, concessionsResponse] = await Promise.all([
        qualityAPI.getConcessionStats(statsParams),
        qualityAPI.getConcessions(concessionParams),
      ]);

      setStats(statsResponse.data);
      setConcessions(concessionsResponse.data.results);
      pagination.setFromResponse(concessionsResponse.data.count, (concessionsResponse.data.results || []).length);
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to load concessions data'));
    } finally {
      setLoading(false);
    }
  }, [pagination.currentPage, searchTerm, filters, sortField, sortDirection, isSupplier, viewKey]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleViewConcession = (concessionId: number) => {
    navigate(`/quality/concession/${concessionId}`);
  };

  /**
   * Create the concession the dialog is holding.
   *
   * A failure here is the dialog's problem, not the page's: it reports through
   * `createError` and leaves `showCreateDialog` and `newConcession` untouched,
   * so the user sees what went wrong with everything they typed still in front
   * of them. Writing it to the page-level `error` instead swapped the whole
   * page for the red "Error:" panel — which unmounted the dialog and threw the
   * form away on every 400.
   */
  const handleCreateConcession = async () => {
    try {
      setCreateLoading(true);
      setCreateError(null);
      await qualityAPI.createConcession({
        // `supplier` is deliberately absent — the backend stamps the caller's
        // active supplier context on create and ignores anything sent here.
        customer: parseInt(newConcession.customer, 10),
        part_number: newConcession.part_number,
        part_description: newConcession.part_description,
        // CR-7262: parseFloat, not parseInt -- the column carries decimals.
        quantity_affected: parseFloat(newConcession.quantity_affected),
        deviation_type: newConcession.deviation_type as ConcessionDeviationType,
        title: newConcession.title,
        description: newConcession.description,
        reason: newConcession.reason,
        proposed_disposition: newConcession.proposed_disposition,
        risk_level: newConcession.risk_level as ConcessionRiskLevel,
        risk_justification: newConcession.risk_justification,
      });
      setShowCreateDialog(false);
      setNewConcession(INITIAL_FORM);
      setCreateError(null);
      toast.success('Concession created');
      fetchData();
    } catch (err: unknown) {
      // apiErrorMessage digs out `details.fields.<field>[0]`, so a DRF field
      // error reads "This field is required." rather than "Request failed
      // with status code 400".
      const message = apiErrorMessage(err, 'Failed to create concession');
      setCreateError(message);
      toast.error(message);
    } finally {
      setCreateLoading(false);
    }
  };

  /** Clear the last create failure when the dialog is opened or dismissed. */
  const handleCreateDialogOpenChange = (next: boolean) => {
    setCreateError(null);
    setShowCreateDialog(next);
  };

  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
    pagination.reset();
  };

  const handleFiltersChange = (next: ConcessionFiltersState) => {
    setFilters(next);
    pagination.reset();
  };

  return {
    // Navigation
    navigate,

    // User info
    isSupplier,
    isCustomer,

    // Loading states
    loading,
    error,
    createLoading,
    createError,

    // Data
    stats,
    concessions,
    searchTerm,
    filters,
    sortField,
    sortDirection,
    pagination,

    // Create dialog
    showCreateDialog,
    setShowCreateDialog: handleCreateDialogOpenChange,
    newConcession,
    setNewConcession,

    // Actions
    fetchData,
    handleSort,
    handleViewConcession,
    handleCreateConcession,
    handleSearchChange,
    handleFiltersChange,
  };
}
